import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Card,
  Typography,
  Row,
  Col,
  Radio,
  Switch,
  Space,
  Table,
  message,
  Divider,
  Modal,
  Tag,
  Spin,
  AutoComplete,
} from 'antd';
import { 
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';
import WordSelectionContent from '@/components/Modal/WordSelectionContent';
import WordTable from '@/components/organisms/Table/WordTable';
import { adminWordExamApi, adminWordApi, adminWordbookApi, academyWordExamApi } from '@/services/api';

const { Title } = Typography;

// 단어집 선택 모달 컴포넌트
interface WordbookSelectionModalProps {
  open: boolean;
  onCancel: () => void;
  onOk: (selectedIds: number[]) => void;
  wordbooks: Array<{ id: number; title: string; isPublic: boolean; registrant: string; createdAt: string; wordCount?: number }>;
  loading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  onSearch: () => void;
}

const WordbookSelectionModal: React.FC<WordbookSelectionModalProps> = ({
  open,
  onCancel,
  onOk,
  wordbooks,
  loading,
  search,
  onSearchChange,
  onSearch,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [previewWords, setPreviewWords] = useState<WordData[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedRowKeys([]);
      setPreviewWords([]);
    }
  }, [open]);

  // 선택된 단어장의 단어들을 미리보기
  useEffect(() => {
    const loadPreviewWords = async () => {
      if (!selectedRowKeys.length) {
        setPreviewWords([]);
        return;
      }
      
      setPreviewLoading(true);
      try {
        const results = await Promise.all(selectedRowKeys.map(async (id) => {
          try {
            const res: any = await adminWordbookApi.detail(id);
            const data = res?.data || res;
            const wordsArr = (data?.words || []) as Array<any>;
            return wordsArr.map((w: any) => ({
              id: String(w.id ?? w.wordId ?? Math.random()),
              word: w.word,
              meaning: w.meaning,
              ttsType: `${w.gender || ''}/${w.age || ''}/${w.speed || ''}/${w.pitch || ''}`
            })) as WordData[];
          } catch (_) {
            return [] as WordData[];
          }
        }));
        
        const merged = ([] as WordData[]).concat(...results);
        // 중복 제거 (id 기준)
        const dedupMap = new Map<string, WordData>();
        for (const w of merged) {
          if (!dedupMap.has(w.id)) dedupMap.set(w.id, w);
        }
        setPreviewWords(Array.from(dedupMap.values()));
      } catch (_) {
        setPreviewWords([]);
      } finally {
        setPreviewLoading(false);
      }
    };

    loadPreviewWords();
  }, [selectedRowKeys]);

  const columns = [
    {
      title: '순번',
      dataIndex: 'id',
      key: 'no',
      width: 80,
      align: 'center' as const,
      render: (_: any, __: any, index: number) => (wordbooks.length > 0 ? wordbooks.length - index : 0),
    },
    {
      title: '단어집명',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: '단어 수',
      dataIndex: 'wordCount',
      key: 'wordCount',
      width: 100,
      align: 'center' as const,
      render: (count?: number) => typeof count === 'number' ? `${count}개` : '-',
    },
    {
      title: '등록자',
      dataIndex: 'registrant',
      key: 'registrant',
      width: 120,
    },
  ];

  const wordPreviewColumns = [
    {
      title: '순번',
      key: 'no',
      width: 80,
      align: 'center' as const,
      render: (_: any, __: any, index: number) => (previewWords.length > 0 ? previewWords.length - index : 0),
    },
    {
      title: '단어',
      dataIndex: 'word',
      key: 'word',
    },
    {
      title: '해석',
      dataIndex: 'meaning',
      key: 'meaning',
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => {
      setSelectedRowKeys(keys as number[]);
    },
  };

  const handleOk = () => {
    onOk(selectedRowKeys);
    setSelectedRowKeys([]);
    setPreviewWords([]);
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      okText={`선택 (${selectedRowKeys.length}개)`}
      cancelText="취소"
      width={1200}
      destroyOnClose
      title="단어집 선택"
    >
      <div className='space-y-4'>
        <div className='flex gap-2 items-center mb-4'>
          <Input
            placeholder="단어집 제목 검색"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            onPressEnter={onSearch}
            size="large"
          />
          <Button onClick={onSearch} size="large">검색</Button>
        </div>

        <div>
          <div style={{ marginBottom: 8, fontWeight: 600, fontSize: '14px' }}>단어집 목록</div>
          <Table
            rowSelection={rowSelection}
            columns={columns}
            dataSource={wordbooks}
            rowKey="id"
            loading={loading}
            pagination={false}
            scroll={{ y: 250 }}
            locale={{ emptyText: '단어집이 없습니다.' }}
            onRow={(record) => ({
              onClick: () => {
                const isSelected = selectedRowKeys.includes(record.id);
                if (isSelected) {
                  setSelectedRowKeys(prev => prev.filter(key => key !== record.id));
                } else {
                  setSelectedRowKeys(prev => [...prev, record.id]);
                }
              },
              style: { cursor: 'pointer' }
            })}
          />
        </div>

        {/* 선택된 단어장에 포함된 단어 미리보기 */}
        <div>
          <div style={{ marginBottom: 8, fontWeight: 600, fontSize: '14px' }}>
            포함된 단어 미리보기 ({previewWords.length}개)
          </div>
          {previewLoading ? (
            <div className='flex justify-center items-center py-8 w-full'>
              <Spin />
            </div>
          ) : previewWords.length === 0 ? (
            <div className='py-8 text-center text-gray-400' style={{ background: '#f5f5f5', borderRadius: 8 }}>
              단어집을 선택하면 포함된 단어가 표시됩니다.
            </div>
          ) : (
            <Table
              size='small'
              columns={wordPreviewColumns}
              dataSource={previewWords}
              rowKey="id"
              pagination={false}
              scroll={{ y: 200 }}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

interface WordExamDetail {
  id: number;
  name: string;
  examType: 'word' | 'meaning';
  wordSubmitMethod: 'wordbook' | 'individual';
  words: WordData[];
  wordbookIds?: number[]; // 단어장 ID 목록
  isPublic: boolean;
  registrant?: string;
  registrationDate?: string;
}

interface WordData {
  id: string;
  word: string;
  meaning: string;
  ttsType: string;
}

const AdminWordExamDetail: React.FC = () => {
  const navigate = useNavigate();
  const { wordExamId } = useParams<{ wordExamId: string }>();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [wordExamData, setWordExamData] = useState<WordExamDetail | null>(null);
  const [wordModalVisible, setWordModalVisible] = useState(false);
  // 단어장 선택 모달 상태
  const [wordbookModalVisible, setWordbookModalVisible] = useState(false);
  const [wordbooks, setWordbooks] = useState<Array<{ id: number; title: string; isPublic: boolean; registrant: string; createdAt: string; wordCount?: number }>>([]);
  const [selectedWordbooks, setSelectedWordbooks] = useState<Array<{ id: number; title: string; wordCount?: number }>>([]);
  const [wordbookLoading, setWordbookLoading] = useState(false);
  const [wordbookSearch, setWordbookSearch] = useState('');
  const [words, setWords] = useState<WordData[]>([]);
  const [searchWord, setSearchWord] = useState<string>('');
  const [filteredWords, setFilteredWords] = useState<WordData[]>([]);
  const [wordSubmitMethod, setWordSubmitMethod] = useState<'wordbook' | 'individual'>('wordbook');
  const [examType, setExamType] = useState<'word' | 'meaning'>('word');
  const questionFieldLabel = examType === 'word' ? '문제 해석 (학생에게 노출)' : '문제 단어 (학생에게 노출)';
  const answerFieldLabel = examType === 'word' ? '정답 단어 (학생이 입력)' : '정답 해석 (학생이 입력)';

  const isEditMode = wordExamId && wordExamId !== 'add';
  
  // 디버깅용 로그
  console.log('wordExamId:', wordExamId);
  console.log('isEditMode:', isEditMode);

  // 샘플 단어 데이터
  const sampleWords: WordData[] = [
    {
      id: '1',
      word: 'apple',
      meaning: '사과',
      ttsType: 'US'
    },
    {
      id: '2',
      word: 'book',
      meaning: '책',
      ttsType: 'US'
    },
    {
      id: '3',
      word: 'cat',
      meaning: '고양이',
      ttsType: 'US'
    },
    {
      id: '4',
      word: 'dog',
      meaning: '개',
      ttsType: 'US'
    },
    {
      id: '5',
      word: 'house',
      meaning: '집',
      ttsType: 'US'
    }
  ];

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (isEditMode && wordExamId) {
          const res = await adminWordExamApi.detail(Number(wordExamId));
          const data = res?.data || res;
          
          // 단어장 ID 목록 파싱
          let wordbookIds: number[] = [];
          try {
            const wbIds = data?.wordbookIds;
            wordbookIds = typeof wbIds === 'string' ? JSON.parse(wbIds) : (Array.isArray(wbIds) ? wbIds : []);
          } catch (_) {
            wordbookIds = [];
          }
          
          // 단어 목록 파싱
          const wordsJson = data?.words;
          let parsedWords: WordData[] = [];
          try {
            parsedWords = typeof wordsJson === 'string' ? JSON.parse(wordsJson) : (Array.isArray(wordsJson) ? wordsJson : []);
          } catch (_) {
            parsedWords = [];
          }
          
          const detail: WordExamDetail = {
            id: data.id,
            name: data.name,
            examType: data.examType,
            wordSubmitMethod: data.wordSubmitMethod,
            words: parsedWords,
            wordbookIds: wordbookIds,
            isPublic: !!data.isPublic,
            registrant: data.registrant,
            registrationDate: data.registrationDate,
          };
          
          setWordExamData(detail);
          setWords(detail.words || []);
          setWordSubmitMethod(detail.wordSubmitMethod);
          setExamType(detail.examType);
          
          // 단어장 정보 로드 (단어집 방식인 경우)
          if (detail.wordSubmitMethod === 'wordbook' && wordbookIds.length > 0) {
            try {
              const wbResults = await Promise.all(wordbookIds.map(async (id) => {
                try {
                  const wbRes = await adminWordbookApi.detail(id);
                  const wbData = wbRes?.data || wbRes;
                  return { id, title: wbData?.title || '', wordCount: Array.isArray(wbData?.words) ? wbData.words.length : 0 };
                } catch (_) {
                  return null;
                }
              }));
              setSelectedWordbooks(wbResults.filter(Boolean) as Array<{ id: number; title: string; wordCount?: number }>);
            } catch (_) {
              // 실패 시 무시
            }
          }
          
          form.setFieldsValue({
            name: detail.name,
            examType: detail.examType,
            wordSubmitMethod: detail.wordSubmitMethod,
            isPublic: detail.isPublic,
            registrant: detail.registrant,
            registrationDate: detail.registrationDate,
          });
        } else {
          setWordExamData(null);
          setWords([]);
          setSelectedWordbooks([]);
          setWordSubmitMethod('wordbook');
          setExamType('word');
          form.resetFields();
          form.setFieldsValue({
            examType: 'word',
            wordSubmitMethod: 'wordbook',
            isPublic: true,
            registrant: '관리자',
          });
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [wordExamId, form, isEditMode]);

  const handleBackToList = () => {
    navigate('/admin/word-exam');
  };

  const handleDownloadTemplate = () => {
    const header = 'word,meaning\n';
    const blob = new Blob([header], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'word-exam-template.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadWordExamPdf = async (type: 'exam' | 'answer') => {
    if (!wordExamId || wordExamId === 'add') {
      message.error('저장된 단어시험이 있어야 다운로드할 수 있습니다.');
      return;
    }
    try {
      const blob = type === 'exam'
        ? await academyWordExamApi.downloadExam(Number(wordExamId))
        : await academyWordExamApi.downloadAnswer(Number(wordExamId));
      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement('a');
      a.href = url;
      a.download = type === 'exam' ? 'word-exam.pdf' : 'word-exam-answer.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.error('PDF 다운로드에 실패했습니다.');
    }
  };

  const handleSubmit = async (values: any) => {
    setSaving(true);
    try {
      const payload: any = {
        name: values.name,
        examType: values.examType,
        wordSubmitMethod: values.wordSubmitMethod,
        isPublic: !!values.isPublic,
        registrant: values.registrant || '관리자',
      };

      if (values.wordSubmitMethod === 'individual') {
        const hasEmpty = words.some((w) => !w.word?.trim() || !w.meaning?.trim());
        if (hasEmpty) {
          message.error('단어와 해석을 모두 입력해주세요.');
          setSaving(false);
          return;
        }
      }
      
      // 단어집 방식이면 wordbookIds 저장
      if (values.wordSubmitMethod === 'wordbook') {
        payload.wordbookIds = JSON.stringify(selectedWordbooks.map(wb => wb.id));
        payload.words = null; // 단어집 방식은 words 필드 비움
      } else {
        // 개별 등록 방식이면 words 저장
        payload.words = JSON.stringify(words);
        payload.wordbookIds = null; // 개별 방식은 wordbookIds 필드 비움
      }
      
      if (isEditMode && wordExamId) {
        await adminWordExamApi.update(Number(wordExamId), payload);
        message.success('단어시험이 수정되었습니다.');
      } else {
        await adminWordExamApi.create(payload);
        message.success('단어시험이 등록되었습니다.');
      }
      navigate('/admin/word-exam');
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || '저장 중 오류가 발생했습니다.';
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/word-exam');
  };

  // const handleWordModalOk = (selectedWords: WordData[]) => {
  //   setWords(prev => [...prev, ...selectedWords]);
  //   setWordModalVisible(false);
  //   message.success(`${selectedWords.length}개의 단어가 추가되었습니다.`);
  // };

  const handleWordModalCancel = () => {
    setWordModalVisible(false);
  };

  const handleRemoveWord = (wordId: string) => {
    setWords(prev => prev.filter(word => word.id !== wordId));
    message.success('단어가 제거되었습니다.');
  };

  const handleSearchWord = () => {
    if (!searchWord.trim()) {
      message.warning('검색할 단어를 입력해주세요.');
      return;
    }
    
    const filtered = sampleWords.filter(word =>
      word.word.toLowerCase().includes(searchWord.toLowerCase()) ||
      word.meaning.toLowerCase().includes(searchWord.toLowerCase())
    );
    
    setFilteredWords(filtered);
    
    if (filtered.length === 0) {
      message.info('검색 결과가 없습니다.');
    } else {
      message.success(`${filtered.length}개의 단어를 찾았습니다.`);
    }
  };

  const handleWordSubmitMethodChange = (value: 'wordbook' | 'individual') => {
    setWordSubmitMethod(value);
  };

  const handleAddWordFromSearch = (word: WordData) => {
    // 이미 추가된 단어인지 확인
    const isAlreadyAdded = words.some(w => w.id === word.id);
    if (isAlreadyAdded) {
      message.warning('이미 추가된 단어입니다.');
      return;
    }
    
    setWords(prev => [...prev, word]);
    message.success('단어가 추가되었습니다.');
  };

  const handleOpenWordSelection = async () => {
    // 단어집 선택 모달 열기
    try {
      setWordbookLoading(true);
      const res: any = await adminWordbookApi.list({ q: wordbookSearch, isPublic: undefined, page: 0, size: 200 });
      const page = res?.data || res;
      const content = page?.content || page?.data?.content || [];
      const mapped = content.map((b: any) => ({
        id: Number(b.id || b.wordbookId || 0),
        title: b.title || b.name || '',
        isPublic: !!(b.isPublic ?? true),
        registrant: b.registrant || '',
        createdAt: b.createdAt || '',
        wordCount: typeof b.itemCount === 'number'
          ? b.itemCount
          : Array.isArray(b.words)
            ? b.words.length
            : undefined,
      }));
      setWordbooks(mapped);
    } catch (_) {
      setWordbooks([]);
    } finally {
      setWordbookLoading(false);
      setWordbookModalVisible(true);
    }
  };

  const handleApplySelectedWordbooks = (selectedIds: number[]) => {
    if (!selectedIds.length) {
      setWordbookModalVisible(false);
      message.info('단어집을 선택하지 않았습니다.');
      return;
    }

    const selected = wordbooks
      .filter(wb => selectedIds.includes(wb.id))
      .map(wb => ({ id: wb.id, title: wb.title, wordCount: wb.wordCount }));

    let addedCount = 0;
    setSelectedWordbooks(prev => {
      const map = new Map(prev.map(wb => [wb.id, wb]));
      selected.forEach(wb => {
        if (!map.has(wb.id)) {
          map.set(wb.id, wb);
          addedCount += 1;
        }
      });
      return Array.from(map.values());
    });

    setWordbookModalVisible(false);

    if (addedCount > 0) {
      message.success(`${addedCount}개의 단어집이 추가되었습니다.`);
    } else {
      message.info('이미 선택된 단어집입니다.');
    }
  };

  const handleRemoveWordbook = (id: number) => {
    setSelectedWordbooks(prev => prev.filter(wb => wb.id !== id));
    message.success('단어집이 제거되었습니다.');
  };

  // 선택된 단어집들의 모든 단어 로드
  const [wordbookWords, setWordbookWords] = useState<WordData[]>([]);
  const [wordbookWordsLoading, setWordbookWordsLoading] = useState(false);
  const [showWordbookWords, setShowWordbookWords] = useState(true);

  useEffect(() => {
    const loadWordbookWords = async () => {
      if (!selectedWordbooks.length) {
        setWordbookWords([]);
        return;
      }

      setWordbookWordsLoading(true);
      try {
        const results = await Promise.all(selectedWordbooks.map(async (wb) => {
          try {
            const res: any = await adminWordbookApi.detail(wb.id);
            const data = res?.data || res;
            const wordsArr = (data?.words || []) as Array<any>;
            return wordsArr.map((w: any) => ({
              id: String(w.id ?? w.wordId ?? Math.random()),
              word: w.word,
              meaning: w.meaning,
              ttsType: `${w.gender || ''}/${w.age || ''}/${w.speed || ''}/${w.pitch || ''}`
            })) as WordData[];
          } catch (_) {
            return [] as WordData[];
          }
        }));

        const merged = ([] as WordData[]).concat(...results);
        // 중복 제거 (id 기준)
        const dedupMap = new Map<string, WordData>();
        for (const w of merged) {
          if (!dedupMap.has(w.id)) dedupMap.set(w.id, w);
        }
        setWordbookWords(Array.from(dedupMap.values()));
      } catch (_) {
        setWordbookWords([]);
      } finally {
        setWordbookWordsLoading(false);
      }
    };

    loadWordbookWords();
  }, [selectedWordbooks]);

  const handleRemoveWordbookWord = (wordId: string) => {
    setWordbookWords(prev => prev.filter(w => w.id !== wordId));
    message.success('단어가 제거되었습니다.');
  };

  // 개별 등록 관련 상태
  const [individualWordInput, setIndividualWordInput] = useState('');
  const [individualMeaningInput, setIndividualMeaningInput] = useState('');
  const [wordSuggestions, setWordSuggestions] = useState<Array<{ value: string; label: string; meaning: string; ttsType: string; wordId: string }>>([]);
  const [searchingWords, setSearchingWords] = useState(false);
  const [showRegisteredWords, setShowRegisteredWords] = useState(true);

  // 단어 검색 (AutoComplete용)
  const handleSearchIndividualWord = async (searchText: string) => {
    setIndividualWordInput(searchText);
    
    if (!searchText || searchText.trim().length < 1) {
      setWordSuggestions([]);
      return;
    }

    setSearchingWords(true);
    try {
      const res: any = await adminWordApi.list({ q: searchText.trim(), page: 0, size: 20 });
      const page = res?.data || res;
      const content = page?.content || page?.data?.content || [];
      
      const suggestions = content.map((w: any) => {
        const ttsType = `${w.gender || 'US'}/${w.age || 'adult'}/${w.speed || '1.0'}/${w.pitch || '1.0'}`;
        return {
          value: w.word || '',
          label: `${w.word || ''} - ${w.meaning || ''} (TTS: ${ttsType})`,
          meaning: w.meaning || '',
          ttsType: ttsType,
          wordId: String(w.id || w.wordId || Math.random()),
        };
      });
      
      setWordSuggestions(suggestions);
    } catch (_) {
      setWordSuggestions([]);
    } finally {
      setSearchingWords(false);
    }
  };

  // 단어 선택 시
  const handleSelectIndividualWord = (value: string, option: any) => {
    setIndividualWordInput(value);
    setIndividualMeaningInput(option.meaning || '');
  };

  // 개별 단어 추가
  const handleAddIndividualWord = () => {
    if (!individualWordInput.trim()) {
      message.warning('단어를 입력해주세요.');
      return;
    }
    if (!individualMeaningInput.trim()) {
      message.warning('해석을 입력해주세요.');
      return;
    }

    const newWord: WordData = {
      id: String(Date.now()),
      word: individualWordInput.trim(),
      meaning: individualMeaningInput.trim(),
      ttsType: 'US/adult/1.0/1.0', // 기본값
    };

    setWords(prev => [...prev, newWord]);
    setIndividualWordInput('');
    setIndividualMeaningInput('');
    setWordSuggestions([]);
    message.success('단어가 추가되었습니다.');
  };



  if (loading) {
    return (
      <AdminLayout>
        <div style={{ padding: '24px', textAlign: 'center', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div>로딩 중...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ padding: '24px' }}>
        {/* 헤더 */}
        <div style={{ marginBottom: 24 }}>
          <Space style={{ marginBottom: 16 }}>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={handleBackToList}
            >
              목록으로
            </Button>
          </Space>
                     <div className='flex justify-between items-center'>
                  <Title level={2}>
                      {isEditMode ? '단어시험 상세' : '단어시험 등록'}
                  </Title>
               
             {/* 상세일시만 노출 */}
             {isEditMode && (
               <div className='flex gap-2'>
                  <Button
                    type='primary'
                    size='large'
                    ghost
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadWordExamPdf('exam')}
                  >
                    시험지 PDF 다운로드
                  </Button>
                  <Button
                    type='primary'
                    size='large'
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadWordExamPdf('answer')}
                  >
                    답지 PDF 다운로드
                  </Button>
               </div>
             )}
               
           </div>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={(changedValues) => {
            if (changedValues.wordSubmitMethod) {
              handleWordSubmitMethodChange(changedValues.wordSubmitMethod);
            }
            if (changedValues.examType) {
              setExamType(changedValues.examType);
            }
          }}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="단어시험명"
                name="name"
                rules={[
                  { required: true, message: '단어시험명을 입력해주세요' }
                ]}
              >
                <Input
                  placeholder="단어시험명을 입력하세요"
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="시험유형"
                name="examType"
                rules={[
                  { required: true, message: '시험유형을 선택해주세요' }
                ]}
              >
                <Radio.Group size="large">
                  <Radio value="word">단어 작성형</Radio>
                  <Radio value="meaning">해석 작성형</Radio>
                </Radio.Group>
              </Form.Item>
              <div className='mb-4 text-sm text-gray-600'>
                {examType === 'word'
                  ? '단어 작성형: 학생에게는 해석이 제시되고, 단어를 직접 입력합니다.'
                  : '해석 작성형: 학생에게는 단어가 제시되고, 해석을 직접 입력합니다.'}
              </div>
            </Col>
                    
            <Col span={12}>
              <Form.Item
                label="단어 제출 방법"
                name="wordSubmitMethod"
                rules={[
                  { required: true, message: '단어 제출 방법을 선택해주세요' }
                ]}
              >
                <Radio.Group size="large">
                  <Radio value="wordbook">단어집에서 가져오기</Radio>
                  <Radio value="individual">단어 개별 등록하기</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            
            {/*단어집에서 가져오기 라디오 클릭시 노출 */}
            {wordSubmitMethod === 'wordbook' && (
              <Col span={24}>
                <Title level={5}>등록할 단어집</Title>
                <div style={{ marginBottom: 16 }}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleOpenWordSelection}
                    size="large"
                  >
                    단어집 불러오기
                  </Button>
                </div>
            
                {/* 선택된 단어집 뱃지 표시 */}
                {selectedWordbooks.length > 0 ? (
                  <div style={{ marginTop: 16, marginBottom: 24 }}>
                    <div style={{ marginBottom: 8, color: '#666', fontSize: '14px' }}>
                      선택된 단어집 ({selectedWordbooks.length}개)
                    </div>
                    <div className='flex flex-wrap gap-2'>
                      {selectedWordbooks.map((wb) => (
                        <Tag
                          key={wb.id}
                          closable
                          onClose={() => handleRemoveWordbook(wb.id)}
                          style={{ padding: '8px 12px', borderRadius: 999, fontSize: '14px' }}
                          color="blue"
                        >
                          <span style={{ fontWeight: 600 }}>{wb.title}</span>
                          {typeof wb.wordCount === 'number' && <span style={{ marginLeft: 8, color: '#888' }}>({wb.wordCount}개)</span>}
                        </Tag>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                    '단어집 불러오기' 버튼을 클릭하여 단어집을 선택하세요.
                  </div>
                )}

                {/* 선택된 단어집에 포함된 단어 리스트 */}
                {selectedWordbooks.length > 0 && (
                  <div style={{ marginTop: 24 }}>
                    <div className='flex gap-4 justify-between items-center'>
                      <Title level={5} style={{ margin: 0 }}>
                        포함된 단어 목록 ({wordbookWords.length}개)
                      </Title>
                      <Button
                        type='link'
                        size='small'
                        onClick={() => setShowWordbookWords((prev) => !prev)}
                      >
                        {showWordbookWords ? '단어 목록 접기' : '단어 목록 펼치기'}
                      </Button>
                    </div>
                    {showWordbookWords && (
                      wordbookWordsLoading ? (
                        <div className='flex justify-center items-center py-8 w-full'>
                          <Spin />
                        </div>
                      ) : wordbookWords.length > 0 ? (
                        <div className='max-h-[400px] overflow-y-auto'>
                          <WordTable
                            data={wordbookWords}
                            showActions={true}
                            showCheckbox={false}
                            showTtsType={false}
                            onDelete={handleRemoveWordbookWord}
                          />
                        </div>
                      ) : (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#999', background: '#f5f5f5', borderRadius: 8 }}>
                          선택된 단어집에 단어가 없습니다.
                        </div>
                      )
                    )}
                  </div>
                )}
              </Col>
            )}

            {/*단어 개별 등록 라디오 클릭시 노출 */}
            {wordSubmitMethod === 'individual' && (
              <Col span={24}>
                <Card>
                  <div className='flex justify-between items-center mb-4'>
                    <Title level={5} style={{ margin: 0 }}>단어 개별 등록</Title>
                    <Button
                      icon={<DownloadOutlined />}
                      size="large"
                      onClick={handleDownloadTemplate}
                    >
                      엑셀 양식 다운로드
                    </Button>
                  </div>
                  <Row gutter={24} align="middle">
                    <Col span={10}>
                      <Form.Item
                        label={examType === 'word' ? answerFieldLabel : questionFieldLabel}
                        help={examType === 'word' ? '학생이 입력해야 하는 정답 단어입니다.' : '학생에게 그대로 노출될 단어입니다.'}
                      >
                        <AutoComplete
                          size='large'
                          value={individualWordInput}
                          options={wordSuggestions}
                          onSearch={handleSearchIndividualWord}
                          onSelect={handleSelectIndividualWord}
                          placeholder="단어를 입력하세요 (자동완성)"
                          notFoundContent={searchingWords ? <Spin size="small" /> : '검색 결과가 없습니다.'}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={10}>
                      <Form.Item
                        label={examType === 'word' ? questionFieldLabel : answerFieldLabel}
                        help={examType === 'word' ? '학생에게 문제로 보여질 해석입니다.' : '학생이 입력해야 하는 정답 해석입니다.'}
                      >
                        <Input 
                          size='large' 
                          value={individualMeaningInput}
                          onChange={(e) => setIndividualMeaningInput(e.target.value)}
                          disabled
                          placeholder="단어 선택 시 자동 입력됩니다"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={4} style={{ marginTop: 8 }}>
                      <Button 
                        type='primary' 
                        size='large' 
                        icon={<PlusOutlined />}
                        onClick={handleAddIndividualWord}
                      >
                        추가
                      </Button>
                    </Col>
                  </Row>
                </Card>

                {/* 등록된 단어 목록 */}
                {words.length > 0 && (
                  <div style={{ marginTop: 24 }}>
                    <div className='flex gap-4 justify-between items-center'>
                      <Title level={5} style={{ margin: 0 }}>등록된 단어 ({words.length}개)</Title>
                      <Button
                        type='link'
                        size='small'
                        onClick={() => setShowRegisteredWords((prev) => !prev)}
                      >
                        {showRegisteredWords ? '등록된 단어 숨기기' : '등록된 단어 보기'}
                      </Button>
                    </div>
                    {showRegisteredWords && (
                      <div className='max-h-[400px] overflow-y-auto'>
                        <WordTable
                          data={words}
                          showActions={true}
                          showCheckbox={false}
                          showTtsType={false}
                          onDelete={handleRemoveWord}
                        />
                      </div>
                    )}
                  </div>
                )}
              </Col>
            )}
            <Divider />

          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="등록자"
                name="registrant"
              >
                <Input
                  disabled
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="등록일"
                name="registrationDate"
              >
                <Input
                  disabled
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="공개 설정"
                name="isPublic"
                valuePropName="checked"
              >
                <Switch checkedChildren="공개" unCheckedChildren="비공개" />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          

          

          {/* 버튼 영역 */}
          <div style={{ textAlign: 'right', marginTop: 32 }}>
            <Space size="middle">
              <Button 
                onClick={handleCancel}
                size="large"
                style={{ height: 48, fontSize: 16, padding: '0 32px' }}
              >
                취소
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={saving}
                size="large"
                style={{ height: 48, fontSize: 16, padding: '0 32px' }}
              >
                {isEditMode ? '수정' : '등록'}
              </Button>
            </Space>
          </div>
        </Form>

        {/* 단어집 선택 모달 */}
        <WordbookSelectionModal
          open={wordbookModalVisible}
          onCancel={() => setWordbookModalVisible(false)}
          onOk={handleApplySelectedWordbooks}
          wordbooks={wordbooks}
          loading={wordbookLoading}
          search={wordbookSearch}
          onSearchChange={setWordbookSearch}
          onSearch={handleOpenWordSelection}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminWordExamDetail; 